import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Divider,
  Box,
  Button,
  Tooltip,
  InputBase,
  ClickAwayListener,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListSubheader,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications,
  Logout,
  Person,
  Settings,
  Search,
} from "@mui/icons-material";
import { AVATAR_COLORS } from "../../types";
import { API_BASE_URL } from "../../api/apiClient";

const formatNotifTime = (timeStr: string) => {
  if (!timeStr) return "";
  try {
    const d = new Date(timeStr);
    if (isNaN(d.getTime())) return timeStr;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return timeStr;
  }
};

interface TopbarProps {
  pageTitle: string;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  notifAnchor: null | HTMLElement;
  setNotifAnchor: (el: null | HTMLElement) => void;
  userAnchor: null | HTMLElement;
  setUserAnchor: (el: null | HTMLElement) => void;
  unseenCount: number;
  allNotifications: any[];
  handleOpenNotifs: (e: React.MouseEvent<HTMLElement>) => void;
  handleMarkAllRead: () => void;
  handleClearNotifs: () => void;
  handleLogout: () => void;
  handleNav: (key: string) => void;
  user: any;
  displayName: string;
  displayInitials: string;
  displayColor: string;
  roleConfig: any;
  profilePic?: string;
}

const Topbar: React.FC<TopbarProps> = ({
  pageTitle,
  sidebarOpen,
  toggleSidebar,
  notifAnchor,
  setNotifAnchor,
  userAnchor,
  setUserAnchor,
  unseenCount,
  allNotifications,
  handleOpenNotifs,
  handleMarkAllRead,
  handleClearNotifs,
  handleLogout,
  handleNav,
  user,
  displayName,
  displayInitials,
  displayColor,
  roleConfig,
  profilePic,
}) => {
  const navigate = useNavigate();
  const employees = useAppSelector((s: any) => s.data?.employees || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const imgSrc = profilePic
    ? profilePic.startsWith("http") || profilePic.startsWith("data:")
      ? profilePic
      : `${API_BASE_URL.replace("/api", "")}${profilePic}`
    : undefined;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const searchPages = [
    { label: "Dashboard", path: "dashboard" },
    { label: "Employees", path: "employees" },
    { label: "Attendance", path: "attendance" },
    { label: "Leave", path: "leave" },
    { label: "Timesheets", path: "timesheets" },
    { label: "Payroll", path: "payroll" },
    { label: "Settings", path: "settings" },
    { label: "My Profile", path: "myprofile" },
    { label: "Company Documents", path: "company-documents" },
  ];

  const filteredPages = searchPages.filter((p) =>
    p.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const filteredEmployees = employees
    .filter(
      (e: any) =>
        e.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.sysId?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .slice(0, 5);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: "rgba(255, 255, 255, 0.75)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(226,232,240,0.6)",
        boxShadow: "0 4px 24px -8px rgba(15,23,42,0.05)",
        color: "#0f172a",
        top: 0,
        zIndex: 1100,
        transition: "all 0.3s ease",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          minHeight: "72px !important",
          height: "72px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <IconButton
            onClick={toggleSidebar}
            edge="start"
            sx={{
              mr: { xs: 1.5, sm: 2.5 },
              color: "#475569",
              background: "#ffffff",
              border: "1px solid rgba(226, 232, 240, 0.8)",
              boxShadow: "0 2px 4px rgba(15,23,42,0.02)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "#f8fafc",
                color: "#0f172a",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 6px rgba(15,23,42,0.04)",
              },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: { xs: 18, sm: 22 },
                fontWeight: 800,
                letterSpacing: "-0.03em",
                background: "linear-gradient(90deg, #0f172a 0%, #334155 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: 1.2,
              }}
            >
              {pageTitle}
            </Typography>
            <Box
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                alignItems: "center",
                px: 1.5,
                py: 0.3,
                background:
                  "linear-gradient(135deg, rgba(15,118,110,0.1), rgba(20,184,166,0.1))",
                color: "#0f766e",
                borderRadius: "6px",
                fontSize: 11,
                fontWeight: 700,
                border: "1px solid rgba(20,184,166,0.2)",
              }}
            >
              Overview & Management
            </Box>
          </Box>
        </Box>

        {/* Middle Search Bar */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flex: 1,
            justifyContent: "center",
            position: "relative",
          }}
        >
          <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
            <Box sx={{ width: "100%", maxWidth: 400, position: "relative" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  borderRadius: "12px",
                  px: 2,
                  py: 0.8,
                  transition: "all 0.2s ease",
                  ...(searchOpen && searchQuery
                    ? {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        background: "#ffffff",
                        border: "1px solid #0ea5e9",
                        borderBottom: "1px solid #e2e8f0",
                      }
                    : {
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        "&:focus-within": {
                          background: "#ffffff",
                          border: "1px solid #0ea5e9",
                          boxShadow: "0 0 0 3px rgba(14,165,233,0.1)",
                        },
                      }),
                }}
              >
                <Search sx={{ color: "#94a3b8", fontSize: 20, mr: 1 }} />
                <InputBase
                  inputRef={searchInputRef}
                  placeholder="Search anything..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  sx={{
                    flex: 1,
                    fontSize: 14,
                    color: "#334155",
                    fontWeight: 500,
                  }}
                />
                <Box
                  sx={{
                    ml: 1,
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    px: 1,
                    py: 0.3,
                    borderRadius: 1.5,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#64748b",
                  }}
                >
                  ⌘K
                </Box>
              </Box>

              {/* Search Results Dropdown */}
              {searchOpen && searchQuery.trim().length > 0 && (
                <Paper
                  elevation={4}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: 12,
                    borderBottomRightRadius: 12,
                    overflow: "hidden",
                    border: "1px solid #0ea5e9",
                    borderTop: "none",
                    zIndex: 1200,
                  }}
                >
                  <List sx={{ p: 0, maxHeight: 400, overflow: "auto" }}>
                    {filteredPages.length > 0 && (
                      <>
                        <ListSubheader
                          sx={{
                            lineHeight: "30px",
                            fontSize: 11,
                            background: "#f8fafc",
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          Modules & Pages
                        </ListSubheader>
                        {filteredPages.map((p) => (
                          <ListItemButton
                            key={p.path}
                            onClick={() => {
                              handleNav(p.path);
                              setSearchOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <ListItemText
                              primary={p.label}
                              primaryTypographyProps={{
                                fontSize: 14,
                                fontWeight: 500,
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </>
                    )}

                    {filteredEmployees.length > 0 && (
                      <>
                        <ListSubheader
                          sx={{
                            lineHeight: "30px",
                            fontSize: 11,
                            background: "#f8fafc",
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          Employees
                        </ListSubheader>
                        {filteredEmployees.map((emp: any) => (
                          <ListItemButton
                            key={emp._id}
                            onClick={() => {
                              navigate(`/app/employees/${emp._id}`);
                              setSearchOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <ListItemAvatar sx={{ minWidth: 40 }}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  fontSize: 12,
                                  bgcolor:
                                    AVATAR_COLORS[
                                      emp.avColor as keyof typeof AVATAR_COLORS
                                    ] || AVATAR_COLORS.blue,
                                }}
                              >
                                {emp.initials || "?"}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={emp.name}
                              secondary={emp.email}
                              primaryTypographyProps={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#0f172a",
                              }}
                              secondaryTypographyProps={{
                                fontSize: 11,
                                noWrap: true,
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </>
                    )}

                    {filteredPages.length === 0 &&
                      filteredEmployees.length === 0 && (
                        <Box sx={{ p: 3, textAlign: "center" }}>
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                            No results found for "{searchQuery}"
                          </Typography>
                        </Box>
                      )}
                  </List>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flex: 1,
            gap: { xs: 1, sm: 1 },
          }}
        >
          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              onClick={handleOpenNotifs}
              sx={{
                color: "#64748b",
                transition: "all 0.2s ease",
                background: notifAnchor
                  ? "rgba(241, 245, 249, 1)"
                  : "transparent",
                "&:hover": {
                  background: "rgba(241, 245, 249, 0.8)",
                  color: "#0f172a",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Badge
                color="error"
                badgeContent={unseenCount}
                invisible={unseenCount === 0}
                sx={{
                  "& .MuiBadge-badge": {
                    boxShadow: "0 0 0 2px #ffffff",
                    right: 4,
                    top: 4,
                    fontSize: 9,
                    minWidth: 12,
                    height: 12,
                    padding: "0 2px",
                  },
                }}
              >
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={notifAnchor}
            open={Boolean(notifAnchor)}
            onClose={() => setNotifAnchor(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  width: 380,
                  borderRadius: 3,
                  border: "1px solid rgba(226,232,240,0.8)",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                  mt: 1.5,
                  overflow: "hidden",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 2,
                background: "rgba(248,250,252,0.8)",
                backdropFilter: "blur(8px)",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}
                >
                  Notifications
                </Typography>
                {unseenCount > 0 && (
                  <Box
                    sx={{
                      background: "#ef4444",
                      color: "white",
                      px: 1,
                      py: 0.2,
                      borderRadius: 99,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {unseenCount} new
                  </Box>
                )}
              </Box>
              {allNotifications.length > 0 && (
                <Button
                  size="small"
                  onClick={handleMarkAllRead}
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "none",
                    color: "#3b82f6",
                    p: 0,
                    minWidth: "auto",
                    "&:hover": { color: "#2563eb", background: "transparent" },
                  }}
                >
                  Read all
                </Button>
              )}
            </Box>

            <Box sx={{ maxHeight: 310, overflowY: "auto" }}>
              {allNotifications.length === 0 ? (
                <Box
                  sx={{
                    p: 5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Notifications sx={{ color: "#94a3b8" }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748b", fontWeight: 500 }}
                  >
                    You're all caught up!
                  </Typography>
                </Box>
              ) : (
                allNotifications.map((n) => (
                  <MenuItem
                    key={n.id}
                    sx={{
                      px: 2,
                      py: 1,
                      alignItems: "flex-start",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      background: n.read
                        ? "transparent"
                        : "rgba(133,209,219,0.06)",
                      borderBottom: "1px solid #f1f5f9",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: n.read
                          ? "#f8fafc"
                          : "rgba(133,209,219,0.12)",
                      },
                    }}
                  >
                    {!n.read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: "#85D1DB",
                          mt: 0.7,
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Box sx={{ ml: n.read ? 2.5 : 0, flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: n.read ? "#475569" : "#0f172a",
                          lineHeight: 1.4,
                          fontWeight: n.read ? 500 : 600,
                        }}
                      >
                        {n.message}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "#94a3b8",
                          mt: 0.5,
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {formatNotifTime(n.time)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Box>
            {allNotifications.length > 0 && (
              <Box
                sx={{
                  p: 1.5,
                  borderTop: "1px solid #e2e8f0",
                  textAlign: "center",
                  background: "#f8fafc",
                }}
              >
                <Button
                  size="small"
                  onClick={handleClearNotifs}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#ef4444",
                    "&:hover": { background: "#fef2f2" },
                  }}
                >
                  Clear all notifications
                </Button>
              </Box>
            )}
          </Menu>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ my: 2.5, borderColor: "rgba(226,232,240,0.8)" }}
          />

          {/* User avatar */}
          <Box
            sx={{
              p: "2px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #0ea5e9, #10b981)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor: "pointer",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 4px 12px rgba(16,185,129,0.2)",
              },
            }}
            onClick={(e) => setUserAnchor(e.currentTarget)}
          >
            {user && imgSrc && (
              <Box
                component="img"
                src={imgSrc}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  objectFit: "cover",
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#fff",
                  border: "2px solid #ffffff",
                }}
              />
            )}
            {user && !imgSrc && (
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background:
                    AVATAR_COLORS[displayColor as keyof typeof AVATAR_COLORS] ||
                    AVATAR_COLORS.blue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#fff",
                  border: "2px solid #ffffff",
                }}
              >
                {displayInitials}
              </Box>
            )}
          </Box>
          <Menu
            anchorEl={userAnchor}
            open={Boolean(userAnchor)}
            onClose={() => setUserAnchor(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  width: 260,
                  borderRadius: 3,
                  border: "1px solid rgba(226,232,240,0.8)",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                  mt: 1.5,
                },
              },
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                background:
                  "linear-gradient(135deg, rgba(133,209,219,0.1), rgba(182,242,209,0.1))",
                borderBottom: "1px solid rgba(226,232,240,0.8)",
                mb: 1,
              }}
            >
              {user && imgSrc && (
                <Box
                  component="img"
                  src={imgSrc}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    objectFit: "cover",
                    background: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(15,23,42,0.12)",
                    border: "2px solid #ffffff",
                    flexShrink: 0,
                  }}
                />
              )}
              {user && !imgSrc && (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background:
                      AVATAR_COLORS[
                        displayColor as keyof typeof AVATAR_COLORS
                      ] || AVATAR_COLORS.blue,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(15,23,42,0.12)",
                    border: "2px solid #ffffff",
                    flexShrink: 0,
                  }}
                >
                  {displayInitials}
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.2,
                  }}
                >
                  {displayName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#64748b",
                    fontWeight: 500,
                    mt: 0.5,
                    wordBreak: "break-all",
                    textTransform: "lowercase",
                  }}
                >
                  {user?.email || "user@example.com"}
                </Typography>
                <Box
                  sx={{
                    display: "inline-block",
                    mt: 0.8,
                    px: 1,
                    py: 0.3,
                    background: "rgba(15,23,42,0.06)",
                    borderRadius: 1,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {roleConfig?.label}
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: 1, py: 0.5 }}>
              <MenuItem
                sx={{
                  py: 1.2,
                  px: 2,
                  borderRadius: 1.5,
                  mb: 0.5,
                  fontWeight: 500,
                  color: "#334155",
                  "&:hover": { background: "rgba(241,245,249,0.8)" },
                }}
                onClick={() => {
                  handleNav("myprofile");
                  setUserAnchor(null);
                }}
              >
                <Person sx={{ mr: 2, fontSize: 20, color: "#64748b" }} /> My
                Profile
              </MenuItem>
              {user?.role.toLowerCase() === "admin" && (
                <MenuItem
                  sx={{
                    py: 1.2,
                    px: 2,
                    borderRadius: 1.5,
                    mb: 0.5,
                    fontWeight: 500,
                    color: "#334155",
                    "&:hover": { background: "rgba(241,245,249,0.8)" },
                  }}
                  onClick={() => {
                    handleNav("settings");
                    setUserAnchor(null);
                  }}
                >
                  <Settings sx={{ mr: 2, fontSize: 20, color: "#64748b" }} />{" "}
                  Settings
                </MenuItem>
              )}
            </Box>

            <Divider sx={{ my: 0.5, borderColor: "rgba(226,232,240,0.8)" }} />

            <Box sx={{ px: 1, py: 0.5 }}>
              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1.2,
                  px: 2,
                  borderRadius: 1.5,
                  color: "#ef4444",
                  fontWeight: 600,
                  "&:hover": { background: "#fef2f2" },
                }}
              >
                <Logout sx={{ mr: 2, fontSize: 20 }} /> Sign Out
              </MenuItem>
            </Box>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Topbar;
